import { PropsWithChildren, RefObject, useLayoutEffect, useRef, useState } from "react";
import { DateTime } from "luxon";
import { Icon } from "swing-components";

import { translate } from "~utils";

import { ThreadedData } from "./types";
import { buildDates } from "./utils";

/** Layout constants used to work out how many dates fit on screen. */
const GAP = 8;
const CARD_HEIGHT = 58;
/** Cards grow to fill the track, so this is the narrowest one is allowed to get. */
const CARD_MIN_WIDTH = 58;
const ALL_DATES_WIDTH = 72;
const CHEVRON_WIDTH = 32;

type DateReelProps = Pick<ThreadedData, "page" | "ls" | "setLs">;

/**
 * Track the rendered content width of an element, updating on resize.
 *
 * @returns a ref to attach to the element, and its current content width in
 * pixels (`0` until the first measurement lands).
 */
function useElementWidth<T extends HTMLElement>(): [RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);

  // A layout effect rather than a plain effect, so the first measurement lands
  // before the browser paints. Measuring after paint shows a frame of the reel
  // at the `width === 0` fallback of a single date card before it settles.
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    // The content box, not the border box: children are laid out inside the
    // track's horizontal padding, so `getBoundingClientRect` would overstate
    // the space available by that padding and briefly fit a card too many.
    const measure = () => {
      const { paddingLeft, paddingRight } = getComputedStyle(element);
      setWidth(element.clientWidth - parseFloat(paddingLeft) - parseFloat(paddingRight));
    };

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    measure();

    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

/**
 * A horizontally paging reel of weekdays, pinned to the left by an "All Dates"
 * chip that clears the filter. Tapping a day replaces the selection.
 *
 * The number of days on screen is derived from the rendered width rather than a
 * breakpoint, so the reel fills whatever space it is given.
 */
export function DateReel(props: DateReelProps) {
  const { page, ls, setLs } = props;

  const [trackRef, trackWidth] = useElementWidth<HTMLDivElement>();
  // `undefined` means "not paged yet", which anchors the reel at the end of
  // the range on the past page and the start of it on the active page.
  const [pagedStart, setPagedStart] = useState<number>();

  const dates = buildDates(page, DateTime.now());

  const available = trackWidth - ALL_DATES_WIDTH - 2 * (CHEVRON_WIDTH + GAP) - GAP;
  const visibleCount = Math.min(
    dates.length,
    Math.max(1, Math.floor((available + GAP) / (CARD_MIN_WIDTH + GAP))),
  );

  const maxStart = Math.max(0, dates.length - visibleCount);
  const defaultStart = page === "past" ? maxStart : 0;
  const start = Math.min(pagedStart ?? defaultStart, maxStart);

  const visibleDates = dates.slice(start, start + visibleCount);

  const canPageBack = start > 0;
  const canPageForward = start < maxStart;

  const shiftWindow = (screenfuls: number) =>
    setPagedStart(Math.min(Math.max(start + screenfuls * visibleCount, 0), maxStart));

  return (
    <div
      className="mw-800"
      ref={trackRef}
      css={{
        display: "flex",
        alignItems: "center",
        gap: GAP,
        marginTop: 16,
        padding: "0 16px",
        "@media (min-width: 900px)": { padding: 0 },
      }}
    >
      <DateCell
        selected={ls.date === undefined}
        fixedWidth={ALL_DATES_WIDTH}
        onClick={() => setLs((old) => ({ ...old, date: undefined }))}
      >
        <span css={{ fontWeight: 700, lineHeight: 1.2, textAlign: "center" }}>
          {translate("DATE_REEL_ALL_DATES")}
        </span>
      </DateCell>

      <div
        aria-hidden
        css={{ flexShrink: 0, alignSelf: "stretch", borderLeft: "1px solid var(--swing-slate400)" }}
      />

      <ChevronButton
        direction="back"
        disabled={!canPageBack}
        onClick={() => shiftWindow(-1)}
        label={translate("DATE_REEL_PREVIOUS_DATES")}
      />

      <div css={{ display: "flex", gap: GAP, flex: 1, minWidth: 0 }}>
        {visibleDates.map((date) => {
          const isoDate = date.toISODate();
          // Days are derived from `DateTime.now()`, so this only narrows luxon's
          // nullable return type.
          if (!isoDate) return null;

          return (
            <DateCell
              key={isoDate}
              selected={ls.date === isoDate}
              onClick={() => setLs((old) => ({ ...old, date: isoDate }))}
            >
              <span
                css={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                }}
              >
                {date.toFormat("ccc")}
              </span>
              <span css={{ whiteSpace: "nowrap" }}>{date.toFormat("M/d")}</span>
            </DateCell>
          );
        })}
      </div>

      <ChevronButton
        direction="forward"
        disabled={!canPageForward}
        onClick={() => shiftWindow(1)}
        label={translate("DATE_REEL_NEXT_DATES")}
      />
    </div>
  );
}

type DateCellProps = {
  selected: boolean;
  /** Set for "All Dates", which is sized to its label instead of stretching. */
  fixedWidth?: number;
  onClick: () => void;
} & PropsWithChildren;

/**
 * One box in the reel: a stacked weekday/date label that grows to share out any
 * width left over once every visible date has its minimum.
 *
 * Figma: https://www.figma.com/design/uJsnuoosxqn26Rot52zCXZ?node-id=14053-3306
 */
function DateCell(props: DateCellProps) {
  const { selected, fixedWidth, onClick, children } = props;

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      css={{
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        flex: fixedWidth === undefined ? "1 1 0" : "none",
        width: fixedWidth,
        minWidth: fixedWidth ?? CARD_MIN_WIDTH,
        height: CARD_HEIGHT,
        padding: 8,
        borderRadius: 8,
        border: "1px solid var(--swing-blue500)",
        background: selected ? "var(--swing-blue500)" : "var(--swing-blue200)",
        color: selected ? "var(--swing-white200)" : "var(--swing-blue500)",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

type ChevronButtonProps = {
  direction: "back" | "forward";
  disabled: boolean;
  label: string;
  onClick: () => void;
};

function ChevronButton(props: ChevronButtonProps) {
  const { direction, disabled, label, onClick } = props;

  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      css={{
        flexShrink: 0,
        width: CHEVRON_WIDTH,
        height: CHEVRON_WIDTH,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        border: "none",
        borderRadius: 999,
        background: "transparent",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.3 : 1,
      }}
    >
      <Icon name={direction === "back" ? "Chevron Left" : "Chevron Right"} />
    </button>
  );
}
